import { marked } from 'marked';
export class RagProductDetail {
  constructor(element) {
    this.ragField = element.querySelector('.js-rag-input');
    this.ragFieldCode = element.querySelector('.js-rag-inputCode');
    this.ragFieldProductCode = element.querySelector('.js-rag-inputProductCode');
    this.ragResponse = element.querySelector('.js-rag-response');
    this.ragClearButton = element.querySelector('.js-rag-buttonClear');
    this.ragSubmit = element.querySelector('.js-rag-buttonSubmit');
    this.ragVoiceButton = element.querySelector('.js-rag-voiceButton');
    this.ragLoading = element.querySelector('.js-rag-loading');
    this.ragNoResponse = element.querySelector('.js-rag-noResponse');
    this.ragDisplayAfterAnswer = element.querySelectorAll('.js-rag-displayAfterAnswer');
    this.ragExternalDocument = element.querySelector('.js-rag-external-document');
    this.ragDocuments = element.querySelector('.js-rag-documents');
    this.ragVariants = element.querySelector('.js-rag-variants');
    this.suggestionsContainer = element.querySelector('.js-rag-suggestions');
    this.suggestions = element.querySelectorAll('.js-rag-suggestion');
    this.suggestionsTemplate = element.querySelector('.js-rag-suggestions-template');
    this.mainStoreId = element.dataset.mainStoreId || null;
    this.isRagWebCallbackEnabled = element.dataset.isRagWebCallbackEnabled === 'true';
    this.isPristine = true;
    this.isLoading = false;
    this.query = null;
    this.lastPayload = null;
    this.displayed = '';
  }
  async initRag() {
    await this.computeState();
    await this.loadSuggestions();
    this.ragField.addEventListener('input', async () => {
      this.isPristine = false;
      await this.computeState();
    });
    this.ragField.closest('form').addEventListener('submit', async event => {
      event.preventDefault();
      if (this.ragField.value.length === 0) {
        return;
      }
      const inputValue = this.ragField.value;
      const inputProductCodeValue = this.ragFieldProductCode.value;
      this.fetchNDJSON('/services/rag-productdetail', inputValue, inputProductCodeValue, false);
    });
    this.ragClearButton.addEventListener('click', () => {
      this.clearAll();
    });
    this.initVoiceRecognition();
  }
  initVoiceRecognition() {
    if (!this.ragVoiceButton) {
      return;
    }
    const speechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!speechRecognition) {
      return;
    }
    this.ragVoiceButton.classList.remove('mu-visually-hidden');
    this.speechRecognitionConstructor = speechRecognition;
    this.isListening = false;
    this.activeRecognition = null;
    this.ragVoiceButton.addEventListener('click', () => {
      if (this.isListening) {
        this.stopVoiceRecognition();
        return;
      }
      this.startVoiceRecognition();
    });
  }
  createSpeechRecognition(lang) {
    const recognition = new this.speechRecognitionConstructor();
    recognition.lang = lang;
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    return recognition;
  }
  listenOnce(recognition) {
    return new Promise(resolve => {
      recognition.addEventListener('result', event => {
        const result = event.results[0][0];
        resolve({
          transcript: result.transcript.trim(),
          confidence: result.confidence ?? 0
        });
      });
      recognition.addEventListener('error', () => {
        resolve(null);
      });
      recognition.addEventListener('end', () => {
        resolve(null);
      });
      recognition.start();
    });
  }
  async startVoiceRecognition() {
    this.isListening = true;
    this.ragVoiceButton.classList.add('js-rag-voiceButton--listening');
    const lang = document.documentElement.lang || navigator.language;
    this.activeRecognition = this.createSpeechRecognition(lang);
    const result = await this.listenOnce(this.activeRecognition);
    this.isListening = false;
    this.activeRecognition = null;
    this.ragVoiceButton.classList.remove('js-rag-voiceButton--listening');
    if (!result?.transcript) {
      return;
    }
    this.ragField.value = result.transcript;
    this.isPristine = false;
    await this.computeState();
    const inputProductCodeValue = this.ragFieldProductCode.value;
    this.fetchNDJSON('/services/rag-productdetail', result.transcript, inputProductCodeValue, false);
  }
  stopVoiceRecognition() {
    if (this.activeRecognition) {
      this.activeRecognition.stop();
    }
  }
  async computeState() {
    const hasEmptyText = !(this.ragField.value.length > 0);
    this.ragClearButton.classList.toggle('mu-visually-hidden', hasEmptyText);
    if (this.isPristine || hasEmptyText) {
      this.ragSubmit.disabled = true;
    } else {
      this.ragSubmit.disabled = false;
    }
    if (this.isLoading) {
      this.ragLoading.ariaBusy = true;
      this.ragLoading.classList.remove('mu-visually-hidden');
    } else {
      this.ragLoading.ariaBusy = false;
      this.ragLoading.classList.add('mu-visually-hidden');
    }
    if (this.lastPayload) {
      this.ragDisplayAfterAnswer.forEach(el => {
        el.classList.remove('mu-visually-hidden');
      });
      if (this.lastPayload.code) {
        this.ragFieldCode.value = this.lastPayload.code;
      }
      if (this.lastPayload.success === false) {
        const noResponse = this.ragNoResponse.cloneNode(true);
        noResponse.classList.remove('mu-visually-hidden');
        this.ragResponse.innerHTML = '';
        this.ragResponse.appendChild(noResponse);
      } else if (this.lastPayload.answer) {
        await this.write();
        if (this.lastPayload.documents && this.lastPayload.documents.length > 0) {
          this.ragDocuments.innerHTML = '';
          for (const doc of this.lastPayload.documents) {
            const linkElement = this.createLinkElement({
              ...doc,
              title: window.productoffercdp?.translation?.attachments[doc.type.toLowerCase()]
            });
            this.ragDocuments.appendChild(linkElement);
          }
        }
      }
    } else {
      this.ragDisplayAfterAnswer.forEach(el => {
        el.classList.add('mu-visually-hidden');
      });
      this.ragFieldCode.value = '';
      this.ragResponse.innerHTML = '';
      this.ragDocuments.innerHTML = '';
      this.ragVariants.innerHTML = '';
    }
  }
  async clearAll() {
    this.ragField.value = '';
    this.lastPayload = null;
    this.isPristine = true;
    this.isLoading = false;
    await this.computeState();
  }
  createLinkElement(doc) {
    const linkElement = document.createElement('a');
    linkElement.href = doc.url;
    linkElement.className = 'm-ragProductDetailDocument';
    linkElement.target = '_blank';
    linkElement.rel = 'noopener noreferrer';
    const titleDiv = document.createElement('div');
    titleDiv.className = 'm-ragProductDetailDocument__title';
    titleDiv.textContent = doc.title;
    const pageSpan = document.createElement('span');
    pageSpan.className = 'm-ragProductDetailDocument__page';
    pageSpan.textContent = doc.page ? ` #${doc.page}` : '';
    const openDiv = document.createElement('div');
    openDiv.className = 'm-ragProductDetailDocument-open';
    const icon = this.ragExternalDocument.cloneNode(true);
    icon.classList.remove('mu-visually-hidden');
    openDiv.appendChild(icon);
    titleDiv.appendChild(pageSpan);
    linkElement.appendChild(titleDiv);
    linkElement.appendChild(openDiv);
    return linkElement;
  }
  async write() {
    const answer = this.lastPayload?.answer ?? '';
    if (!answer) return;
    if (this.displayed === answer) return;
    const isAppend = answer.startsWith(this.displayed);
    if (!isAppend) this.displayed = '';
    const newPart = answer.slice(this.displayed.length);
    const tokens = newPart.match(/(\S+|\s+)/g) ?? [];
    for (const token of tokens) {
      this.displayed += token;
      this.ragResponse.innerHTML = marked.parse(this.displayed);
      await new Promise(r => setTimeout(r, Math.random() * 50));
    }
  }
  async fetchNDJSON(url, query, productCode, isSuggestion) {
    this.isPristine = true;
    this.isLoading = true;
    this.lastPayload = null;
    this.displayed = '';
    this.query = query;
    await this.computeState();
    const response = await fetch(url, {
      method: 'POST',
      body: JSON.stringify({
        query: query,
        productCode: productCode,
        isSuggestion: isSuggestion
      }),
      headers: {
        Accept: 'application/x-ndjson; charset=utf-8',
        'Content-Type': 'application/json; charset=utf-8'
      }
    });
    if (!response.ok) {
      this.isLoading = false;
      this.lastPayload = {
        success: false
      };
      await this.computeState();
      return;
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';
    while (true) {
      const {
        done,
        value
      } = await reader.read();
      if (done) {
        break;
      }
      buffer += decoder.decode(value, {
        stream: true
      });
      let lines = buffer.split('\n');
      buffer = lines.pop();
      for (const line of lines) {
        if (line.trim() === '') continue;
        try {
          const json = JSON.parse(line);
          this.lastPayload = json;
          if (this.lastPayload.answer) {
            this.isLoading = false;
          }
        } catch (err) {
          this.lastPayload = {
            success: false
          };
          this.isLoading = false;
          console.error('RAG invalid:', line, err);
        } finally {
          await this.computeState();
        }
      }
    }
    if (buffer.trim()) {
      try {
        const json = JSON.parse(buffer);
        this.lastPayload = json;
      } catch (err) {
        this.lastPayload = {
          success: false
        };
        console.error('Last line RAG invalid:', buffer, err);
      }
    }
    this.isLoading = false;
    await this.fetchVariantThumbnails();
    await this.computeState();
    this.triggerWebCallback();
  }
  triggerWebCallback() {
    if (this.isRagWebCallbackEnabled && this.lastPayload.webCallback) {
      window['customer-chatbot']?.EventBus?.emit('web-callback', {
        productCategory: this.lastPayload.productCategory,
        productId: this.lastPayload.productCode,
        productUrl: window.location.href,
        productIsFirstParty: this.lastPayload.isThirdParty,
        storeId: this.mainStoreId
      });
    }
  }
  async fetchVariantThumbnails() {
    if (this.lastPayload.success && this.lastPayload.relationCodes?.length > 0) {
      const response = await fetch('/services/rag-productdetail/variant-thumbnails', {
        method: 'POST',
        body: JSON.stringify({
          variantCodes: this.lastPayload.relationCodes
        }),
        headers: {
          Accept: '*/*',
          'Content-Type': 'application/json; charset=utf-8'
        }
      });
      if (response.ok) {
        this.ragVariants.innerHTML = await response.text();
      }
    }
  }
  async loadSuggestions() {
    const productCode = this.ragFieldProductCode.value;
    if (!productCode || !this.suggestionsContainer) {
      return;
    }
    try {
      const response = await fetch('/services/rag-productdetail/suggestions', {
        method: 'POST',
        body: JSON.stringify({
          productCode: productCode
        }),
        headers: {
          Accept: 'application/x-ndjson; charset=utf-8',
          'Content-Type': 'application/json; charset=utf-8'
        }
      });
      if (!response.ok) {
        console.error('Failed to load suggestions');
        return;
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';
      const allSuggestions = [];
      while (true) {
        const {
          done,
          value
        } = await reader.read();
        if (done) {
          break;
        }
        buffer += decoder.decode(value, {
          stream: true
        });
        const lines = buffer.split('\n');
        buffer = lines.pop();
        for (const line of lines) {
          if (line.trim() === '') continue;
          try {
            const json = JSON.parse(line);
            if (json && Array.isArray(json.suggestions)) {
              allSuggestions.push(...json.suggestions);
            }
          } catch (err) {
            console.error('Suggestions invalid line:', line, err);
          }
        }
      }
      if (buffer.trim()) {
        try {
          const json = JSON.parse(buffer);
          if (json && Array.isArray(json.suggestions)) {
            allSuggestions.push(...json.suggestions);
          }
        } catch (err) {
          console.error('Last line Suggestions invalid:', buffer, err);
        }
      }
      const suggestionsToRender = allSuggestions.map(s => s.trim()).filter(s => s !== '');
      this.renderSuggestions(suggestionsToRender);
    } catch (error) {
      console.error('Error loading suggestions:', error);
    }
  }
  renderSuggestions(suggestions) {
    if (!this.suggestionsTemplate || !this.suggestionsContainer || suggestions.length === 0) {
      return;
    }
    this.suggestionsContainer.innerHTML = '';
    const cloned = this.suggestionsTemplate.cloneNode(true);
    cloned.classList.remove('js-rag-suggestions-template', 'mu-visually-hidden');
    const listWrapper = cloned.querySelector('.js-rag-suggestions-list');
    const tagTemplate = cloned.querySelector('.js-rag-suggestion');
    if (listWrapper && tagTemplate) {
      listWrapper.innerHTML = '';
      suggestions.forEach((suggestion, index) => {
        const tag = tagTemplate.cloneNode(true);
        tag.textContent = suggestion;
        tag.setAttribute('name', `suggestion_${index}`);
        listWrapper.appendChild(tag);
      });
    }
    this.suggestionsContainer.appendChild(cloned);
    this.attachSuggestionListeners();
  }
  attachSuggestionListeners() {
    const suggestionElements = this.suggestionsContainer.querySelectorAll('.js-rag-suggestion');
    suggestionElements.forEach(suggestion => {
      suggestion.addEventListener('click', async e => {
        e.preventDefault();
        const suggestionText = e.currentTarget.textContent.trim();
        if (suggestionText) {
          this.ragField.value = suggestionText;
          this.isPristine = false;
          const inputProductCodeValue = this.ragFieldProductCode.value;
          this.fetchNDJSON('/services/rag-productdetail', suggestionText, inputProductCodeValue, true);
          e.currentTarget.remove();
          if (this.suggestionsContainer.querySelectorAll('.js-rag-suggestion').length === 0) {
            this.suggestionsContainer.remove();
          }
        }
      });
    });
  }
}
export default RagProductDetail;